import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { ApiConfig } from './api.config';
import { METHOD } from './api.enum';
import { ApiBaseTarget, ApiBodyData, ApiDeleteOptions, ApiGetOptions, ApiMutateOptions, ApiQueryParams } from './api.types';

interface ApiRequestErrorInput {
  message: string;
  status: number;
  statusText: string;
  url: string;
  details: unknown;
}

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly statusText: string;
  public readonly url: string;
  public readonly details: unknown;

  constructor({ message, status, statusText, url, details }: ApiRequestErrorInput) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.statusText = statusText;
    this.url = url;
    this.details = details;
  }
}

/**
 * Walks an Error's `cause` chain looking for an ApiRequestError with a 401/403 status.
 * Used to tell "the session is genuinely invalid" apart from transient failures
 * (network drop, API restarting, 5xx) that should not force a logout.
 */
export function isAuthInvalidError(error: unknown): boolean {
  let current: unknown = error;
  while (current) {
    if (current instanceof ApiRequestError) {
      return current.status === 401 || current.status === 403;
    }
    current = current instanceof Error ? current.cause : undefined;
  }
  return false;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly httpClient = inject(HttpClient);

  public async get<T = unknown>({ path, query, params, base = 'api', headers }: ApiGetOptions): Promise<T> {
    return this.request<T>(METHOD.GET, path, undefined, query, params, base, headers);
  }

  public async post<T = unknown>({ path, data, query, params, base = 'api', headers }: ApiMutateOptions): Promise<T> {
    return this.request<T>(METHOD.POST, path, data, query, params, base, headers);
  }

  public async put<T = unknown>({ path, data, query, params, base = 'api', headers }: ApiMutateOptions): Promise<T> {
    return this.request<T>(METHOD.PUT, path, data, query, params, base, headers);
  }

  public async patch<T = unknown>({ path, data, query, params, base = 'api', headers }: ApiMutateOptions): Promise<T> {
    return this.request<T>(METHOD.PATCH, path, data, query, params, base, headers);
  }

  public async delete<T = unknown>({ path, query, base = 'api', headers }: ApiDeleteOptions): Promise<T> {
    return this.request<T>(METHOD.DELETE, path, undefined, query, undefined, base, headers);
  }

  private async request<T = unknown>(
    method: METHOD,
    path: string,
    data?: ApiBodyData,
    query?: ApiQueryParams,
    params?: ApiQueryParams,
    base: ApiBaseTarget = 'api',
    extraHeaders?: Record<string, string>
  ): Promise<T> {
    const url: string = this.makeUrl(path, query, base);
    const requestHeaders: HttpHeaders = this.makeHeaders(extraHeaders);
    const requestParams: HttpParams = this.makeParams(params);
    const request = new HttpRequest<ApiBodyData>(method, url, data ?? null, {
      headers: requestHeaders,
      params: requestParams,
    });
    const request$ = this.httpClient.request<T>(request).pipe(
      filter((response): response is HttpResponse<T> => response instanceof HttpResponse),
      switchMap((response) => this.handleResponse(response)),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
    return lastValueFrom<T>(request$);
  }

  protected handleError(error: HttpErrorResponse): Observable<never> {
    const apiMessage: string = this.extractApiErrorMessage(error);
    const apiError: ApiRequestError = new ApiRequestError({
      message: apiMessage,
      status: error.status,
      statusText: error.message,
      url: error.url ?? '',
      details: error.error,
    });
    return throwError(() => apiError);
  }

  protected handleResponse<T>(response: HttpResponse<T>): Observable<T> {
    return of(response.body);
  }

  private makeUrl(path: string, query?: ApiQueryParams, base: ApiBaseTarget = 'api'): string {
    const baseUrl: string = this.buildBaseUrl(path, base);
    if (!query || Object.keys(query).length === 0) {
      return baseUrl;
    }
    const params: HttpParams = new HttpParams({ fromObject: this.serializeParams(query) });
    const queryString: string = params.toString();
    if (!queryString) {
      return baseUrl;
    }
    const separator: string = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${queryString}`;
  }

  private buildBaseUrl(path: string, base: ApiBaseTarget): string {
    if (this.isAbsoluteUrl(path)) {
      return path;
    }
    const managedBaseUrl: string = ApiConfig.buildBaseUrl(base);
    const normalizedPath: string = path.replace(/^\/+/, '');
    return normalizedPath.length > 0 ? `${managedBaseUrl}/${normalizedPath}` : managedBaseUrl;
  }

  private isAbsoluteUrl(path: string): boolean {
    return /^https?:\/\//i.test(path);
  }

  private extractApiErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 0) {
      return 'Serviço indisponível';
    }
    const errorBody: unknown = error.error;
    if (errorBody && typeof errorBody === 'object') {
      const message: unknown = (errorBody as Record<string, unknown>)['message'];
      if (typeof message === 'string' && message.trim().length > 0) {
        return message;
      }
    }
    return error.message;
  }

  private makeHeaders(extraHeaders?: Record<string, string>): HttpHeaders {
    let headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    if (!extraHeaders) {
      return headers;
    }
    for (const [key, value] of Object.entries(extraHeaders)) {
      headers = headers.set(key, value);
    }
    return headers;
  }

  private makeParams(params?: ApiQueryParams): HttpParams {
    if (!params) return new HttpParams();
    return new HttpParams({ fromObject: this.serializeParams(params) });
  }

  private serializeParams(record: ApiQueryParams): Record<string, string | string[]> {
    return Object.fromEntries(
      Object.entries(record)
        .filter(([, v]) => v !== null && v !== undefined)
        .map(([k, v]) => [k, Array.isArray(v) ? (v as (string | number | boolean)[]).map(String) : String(v)])
    );
  }
}
